import { PostgrestClient } from '@supabase/postgrest-js'
import { config } from '@/config/env'
import { ApiContractError, ApiError, currentToken } from '@/api/http'
import type { ZodType } from 'zod'

/**
 * Lesender Zugang zur Datenbank über PostgREST.
 *
 * Zweite Datenquelle neben der Weltmaschine-API. Sie erspart einen eigenen
 * Endpunkt im Backend für jede einzelne Kennzahl. Wann welche Quelle richtig
 * ist, steht in `docs/data-sources.md`.
 *
 * Die Abfragen bauen auf `@supabase/postgrest-js` auf. Diese Datei ist die
 * Klammer darum und sorgt für drei Dinge, die die Bibliothek von sich aus nicht
 * so macht, wie dieses Projekt es braucht:
 *
 * 1. **Sie wirft.** `postgrest-js` gibt `{ data, error }` zurück. Der Rest des
 *    Projekts arbeitet mit Ausnahmen, und die Wiederholungsregel in
 *    `src/main.tsx` entscheidet anhand von `ApiError`. Ein zurückgegebener
 *    Fehler käme dort nie an, TanStack Query hielte den Aufruf für erfolgreich
 *    und zeigte eine leere Seite ohne Fehlermeldung.
 * 2. **Sie lässt nur lesen.** Der Rückgabetyp von `postgrestFrom` blendet
 *    `insert`, `update`, `upsert` und `delete` aus. Die Datenbankrolle kann sie
 *    ohnehin nicht, ein Aufruf schlüge erst zur Laufzeit fehl. So sagt es
 *    bereits der Compiler.
 * 3. **Sie prüft die Antwort.** Die Datenbank sagt keine Form zu. Jede Antwort
 *    geht durch ein Zod-Schema, genau wie bei der Backend-API.
 *
 * **Ausschließlich lesend, und zwar erzwungen.** Das Token dieser Anwendung
 * trägt den Claim `db_role=insights_readonly`. PostgREST führt damit vor jeder
 * Abfrage `SET ROLE insights_readonly` aus, und diese Rolle hat in der Datenbank
 * nur `SELECT`. Ein Schreibversuch scheitert an PostgreSQL selbst und antwortet
 * mit SQLSTATE `42501`.
 */

/** Die Schemas, die PostgREST nach außen gibt (PGRST_DB_SCHEMAS). */
export type PostgrestSchema = 'pandarizer' | 'd_localization'

const DEFAULT_SCHEMA: PostgrestSchema = 'pandarizer'

/**
 * Macht die Basis-URL absolut.
 *
 * Im Entwicklungsmodus steht in der Konfiguration nur `/db`, damit der Aufruf
 * beim eigenen Origin bleibt und im Vite-Proxy landet. `postgrest-js` baut seine
 * Adressen aber mit `new URL(...)` ohne Basis, und das scheitert an einem
 * relativen Pfad.
 */
function absoluteBaseUrl(): string {
  const base = config.postgrestBaseUrl
  return base.startsWith('/') ? `${window.location.origin}${base}` : base
}

/**
 * Reicht bei jedem Aufruf das aktuelle Token nach.
 *
 * Die Kopfzeilen des Clients werden einmal beim Erzeugen festgelegt. Ein dort
 * eingetragenes Token wäre eingefroren und nach einer stillen Erneuerung
 * abgelaufen, ohne dass es jemandem auffiele. Über eine eigene fetch-Funktion
 * wird es stattdessen pro Anfrage frisch geholt.
 */
const authFetch: typeof fetch = (input, init) => {
  const token = currentToken()
  if (token === undefined) return fetch(input, init)

  const headers = new Headers(init?.headers)
  headers.set('Authorization', `Bearer ${token}`)
  return fetch(input, { ...init, headers })
}

/*
 * Der Typ wird aus dieser Fabrik abgeleitet und nicht hingeschrieben.
 * PostgrestClient ist über den Schemanamen generisch und hat als Vorgabe
 * "public". Ein hingeschriebenes PostgrestClient passte deshalb nicht zu einem
 * Client, der auf "pandarizer" steht.
 */
function createClient(schemaName: PostgrestSchema) {
  return new PostgrestClient(absoluteBaseUrl(), {
    // Setzt Accept-Profile bei lesenden Aufrufen. Ohne diesen Kopf entschiede
    // die Reihenfolge in PGRST_DB_SCHEMAS, einer Umgebungsvariablen im
    // Ansible-Repo, welches Schema gemeint ist.
    schema: schemaName,
    fetch: authFetch,
    /*
     * Wiederholungen gehören an genau eine Stelle, und die ist der QueryClient
     * in src/main.tsx. Blieben beide an, multiplizierten sich die Versuche:
     * drei Anläufe der Bibliothek mal zwei von TanStack Query.
     */
    retry: false,
  })
}

type Client = ReturnType<typeof createClient>

const clients = new Map<PostgrestSchema, Client>()

function clientFor(schemaName: PostgrestSchema): Client {
  const cached = clients.get(schemaName)
  if (cached !== undefined) return cached

  const client = createClient(schemaName)
  clients.set(schemaName, client)
  return client
}

/**
 * Setzt den Client-Zwischenspeicher zurück.
 *
 * Nur für Tests. Die Basis-URL wird beim Erzeugen eingebrannt, ein Test mit
 * anderer Konfiguration bekäme sonst den Client des vorigen.
 */
export function resetPostgrestClients(): void {
  clients.clear()
}

/** Der volle Abfrage-Baustein von postgrest-js, inklusive der Schreibmethoden. */
type QueryBuilder = ReturnType<Client['from']>

/**
 * Derselbe Baustein, aber nur mit `select`.
 *
 * Das ist der Schreibschutz auf Typebene. `insert`, `update`, `upsert` und
 * `delete` existieren in der Bibliothek, sind hier aber nicht erreichbar. Wer
 * sie doch aufriefe, bekäme von der Datenbank ein 42501, und zwar erst zur
 * Laufzeit.
 */
export type ReadOnlyQueryBuilder = Pick<QueryBuilder, 'select'>

/**
 * Beginnt eine lesende Abfrage auf einer Tabelle oder Sicht.
 *
 * ```ts
 * const rows = await runPostgrest(
 *   postgrestFrom('orders')
 *     .select('id,status,created_at')
 *     .eq('status', 'active')
 *     .gte('created_at', '2026-01-01')
 *     .order('created_at', { ascending: false })
 *     .limit(100),
 *   orderRowsSchema,
 * )
 * ```
 */
export function postgrestFrom(
  table: string,
  schemaName: PostgrestSchema = DEFAULT_SCHEMA,
): ReadOnlyQueryBuilder {
  return clientFor(schemaName).from(table)
}

/**
 * Der Fehlerkörper, wie PostgREST ihn liefert.
 *
 * `details` und `hint` sind hier bewusst als nullable deklariert, obwohl
 * `postgrest-js` sie als `string` typisiert. Gegen die echte Instanz geprüft:
 * sie kommen als `null` an. Der Typ der Bibliothek ist an der Stelle zu eng.
 */
interface PostgrestFailure {
  readonly code: string | null
  readonly message: string
  readonly details: string | null
  readonly hint: string | null
}

/** Leere und fehlende Werte einheitlich zu `undefined`. */
function orUndefined(value: string | null): string | undefined {
  return value === null || value.trim() === '' ? undefined : value
}

/** Die Form, in der ein fertiger Abfrage-Baustein sein Ergebnis liefert. */
export interface PostgrestOutcome {
  readonly data: unknown
  readonly error: PostgrestFailure | null
  readonly count: number | null
  readonly status: number
}

/**
 * Fehler eines PostgREST-Aufrufs.
 *
 * Erbt bewusst von `ApiError`: Die Wiederholungsregel in `src/main.tsx` und die
 * Fehleranzeigen der Seiten prüfen darauf. Ein eigener, unverwandter Fehlertyp
 * würde dort durchrutschen.
 *
 * `code` ist entweder ein PostgREST-Code (PGRST…) oder ein SQLSTATE der
 * Datenbank. Beides hilft beim Suchen mehr als der bloße Statuscode.
 */
export class PostgrestError extends ApiError {
  readonly code: string | undefined
  readonly hint: string | undefined

  constructor(status: number, url: string, failure: PostgrestFailure) {
    super(status, url, failure.message)
    this.name = 'PostgrestError'
    this.code = orUndefined(failure.code)
    this.hint = orUndefined(failure.hint)
    this.message = PostgrestError.explain(status, failure)
  }

  private static explain(status: number, failure: PostgrestFailure): string {
    const detail = failure.message.trim()
    const suffix = detail === '' ? '' : ` (${detail})`

    // 42501 ist der SQLSTATE für "insufficient privilege". Hier ist das kein
    // Konfigurationsfehler, sondern der Schreibschutz bei der Arbeit.
    if (failure.code === '42501') {
      return `Die Datenbank hat den Zugriff verweigert. Der Insights-Zugang darf ausschließlich lesen${suffix}`
    }

    switch (status) {
      case 400:
        return `PostgREST hat die Abfrage nicht verstanden. Meist ein Tippfehler in einem Spaltennamen oder Filter${suffix}`
      case 401:
        return `Nicht angemeldet oder die Sitzung ist abgelaufen${suffix}`
      case 403:
        return `Zugriff verweigert. Prüfe, ob das Token den Claim db_role trägt${suffix}`
      case 404:
        return `Die Tabelle oder Sicht existiert nicht, oder sie liegt in einem Schema, das PostgREST nicht ausliefert${suffix}`
      case 406:
        return `PostgREST kann die Anfrage nicht bedienen. Meist ein unbekanntes Schema${suffix}`
      default:
        return `PostgREST hat mit ${String(status)} geantwortet${suffix}`
    }
  }
}

function validate<T>(data: unknown, schema: ZodType<T> | undefined, label: string): T {
  if (schema === undefined) return data as T

  const result = schema.safeParse(data)
  if (result.success) return result.data

  const details = result.error.issues
    .map((issue) => `${issue.path.join('.') || '<Antwort>'}: ${issue.message}`)
    .join('; ')
  throw new ApiContractError(label, details)
}

/**
 * Führt eine Abfrage aus, wirft bei Fehlern und prüft das Ergebnis.
 *
 * Der zweite Parameter ist das Zod-Schema. Ohne ihn erreichen ungeprüfte Daten
 * die Anwendung, und die Datenbank sagt keine Form zu.
 *
 * Der dritte Parameter ist nur eine Beschriftung für Fehlermeldungen, üblich ist
 * der Tabellenname.
 */
export async function runPostgrest<T>(
  query: PromiseLike<PostgrestOutcome>,
  schema?: ZodType<T>,
  label = 'PostgREST',
): Promise<T> {
  const outcome = await query
  if (outcome.error !== null) {
    throw new PostgrestError(outcome.status, label, outcome.error)
  }
  return validate(outcome.data, schema, label)
}

export interface PostgrestPage<T> {
  readonly items: T
  /**
   * Gesamtzahl der Treffer ohne `limit` und `range`.
   *
   * `null`, wenn die Abfrage keine Zählung angefordert hat. Die Zählung wird
   * beim `select` mitgegeben: `.select('id', { count: 'planned' })`.
   *
   * `exact` zählt wirklich durch. Mehrere Tabellen dieser Datenbank haben
   * hunderttausende Zeilen, dort ist das spürbar. Für eine Seitennummerierung
   * reicht `planned`, die Schätzung des Abfrageplaners.
   */
  readonly totalCount: number | null
}

/**
 * Wie `runPostgrest`, liefert zusätzlich die Gesamtzahl.
 *
 * Setzt voraus, dass die Abfrage eine Zählung angefordert hat:
 *
 * ```ts
 * const page = await runPostgrestPage(
 *   postgrestFrom('orders').select('id,status', { count: 'planned' }).limit(25),
 *   orderRowsSchema,
 * )
 * ```
 */
export async function runPostgrestPage<T>(
  query: PromiseLike<PostgrestOutcome>,
  schema?: ZodType<T>,
  label = 'PostgREST',
): Promise<PostgrestPage<T>> {
  const outcome = await query
  if (outcome.error !== null) {
    throw new PostgrestError(outcome.status, label, outcome.error)
  }
  return { items: validate(outcome.data, schema, label), totalCount: outcome.count }
}
